import React, { FC, ElementType, useImperativeHandle, forwardRef } from 'react';
import { alpha, useTheme, InputProps, TextField } from '@mui/material';
import { CardNumberElementProps } from '@stripe/react-stripe-js';
import { StandardTextFieldProps } from '@mui/material';

interface StripeInputProps
  extends CardNumberElementProps,
    Omit<
      InputProps,
      'onFocus' | 'onBlur' | 'onChange' | 'onReady' | 'onEscape'
    > {
  component: ElementType;
}

export const StripeInput: FC<StripeInputProps> = forwardRef((props, ref) => {
  const {
    component: Component,
    inputRef: _inputRef,
    'aria-invalid': _ariaInvalid,
    'aria-describedby': _ariaDescribeBy,
    defaultValue: _defaultValue,
    required: _required,
    onKeyDown: _onKeyDown,
    onKeyUp: _onKeyUp,
    readOnly: _readOnly,
    autoComplete: _autoComplete,
    autoFocus: _autoFocus,
    type: _type,
    name: _name,
    rows: _rows,
    disabled,
    ...other
  } = props;
  const theme = useTheme();
  const [mountNode, setMountNode] = React.useState<HTMLDivElement | null>(null);

  useImperativeHandle(
    ref,
    () => {
      return {
        focus: () => {
          if (!mountNode) {
            return;
          }
          mountNode.focus();
        }
      };
    },
    [mountNode]
  );

  return (
    <Component
      onReady={setMountNode}
      options={{
        disabled,
        style: {
          base: {
            color: theme.palette.text.primary,
            fontSize: `${theme.typography.fontSize}px`,
            fontFamily: theme.typography.fontFamily,
            '::placeholder': {
              color: alpha(theme.palette.text.primary, 0.42)
            }
          },
          invalid: {
            color: theme.palette.text.primary
          }
        }
      }}
      {...other}
    />
  );
});

export interface StripeTextFieldProps
  extends CardNumberElementProps,
    Omit<
      StandardTextFieldProps,
      'onFocus' | 'onBlur' | 'onChange' | 'onReady' | 'onEscape'
    > {
  stripeElement: ElementType;
}

export const StripeTextField: FC<StripeTextFieldProps> = (props) => {
  const { InputLabelProps, stripeElement, InputProps, ...other } = props;

  return (
    <TextField
      fullWidth={true}
      InputLabelProps={{
        ...InputLabelProps,
        shrink: true
      }}
      InputProps={{
        ...InputProps,
        inputProps: {
          component: stripeElement
        },
        // @ts-ignore
        inputComponent: StripeInput
      }}
      {...other}
    />
  );
};

export default StripeTextField;
